import { useEffect, useState } from "react";
import { Box, Button, Paper, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getPushKey, getPushStatus, sendPushTest, subscribeToPush, unsubscribeFromPush } from "../../service/PushService";

/** Base64 (VAPID) -> det format browseren vil have nøglen i */
const toKeyBytes = (base64: string) => {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/**
 * Beskeder på denne enhed: alarmer (fejlet backup, fejl i loggen, cron-jobs) kommer som
 * push på telefonen, når admin er installeret som app. Slås til pr. enhed.
 */
const PushNotifications = () => {
  const [t] = useTranslation("global");
  const [enabledOnServer, setEnabledOnServer] = useState(false);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!supported()) return;
    getPushStatus().then((status) => setEnabledOnServer(Boolean(status?.enabled)));
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => setOn(Boolean(subscription)))
      .catch(() => undefined);
  }, []);

  if (!supported() || !enabledOnServer) return null;

  const turnOn = async () => {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      setMessage(t("push.denied"));
      return;
    }
    const key = await getPushKey();
    if (!key?.publicKey) {
      setMessage(t("push.notConfigured"));
      return;
    }
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKeyBytes(key.publicKey) });
    await subscribeToPush(subscription.toJSON());
    setOn(true);
    setMessage(t("push.on"));
  };

  const turnOff = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await unsubscribeFromPush(subscription.endpoint);
      await subscription.unsubscribe();
    }
    setOn(false);
    setMessage(t("push.off"));
  };

  const toggle = async () => {
    setBusy(true);
    setMessage("");
    try {
      await (on ? turnOff() : turnOn());
    } catch (error: any) {
      setMessage(error?.message || t("push.failed"));
    }
    setBusy(false);
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="body2">{t("push.title")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("push.help")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {on && (
            <Button size="small" onClick={() => sendPushTest().then(() => setMessage(t("push.testSent")))}>
              {t("push.test")}
            </Button>
          )}
          <Switch checked={on} onChange={toggle} disabled={busy} inputProps={{ "aria-label": t("push.title") }} />
        </Box>
      </Box>
      {message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {message}
        </Typography>
      )}
    </Paper>
  );
};

export default PushNotifications;
